import React, { ReactElement, useMemo, useState } from "react";
import { Box } from "@mui/material";
import StewartTable, {
  DetailsViewConfig,
  SelectionConfig,
  StewartTableColumn,
} from "controls/global/stewart-table/StewartTable";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";
import StewartTableFooterPaginator from "controls/global/stewart-table/StewartTableFooterPaginator";
import { Order } from "controls/global/stewart-table/stewartTableHelper";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { useSteppedProgress } from "utils/custom-hooks/useSteppedProgress";
import PolicyDetailsPanel from "../quality-review/PolicyDetailsPanel";

interface CloseQueueTableProps {
  records: PolicyManagementRecord[];
  selectedIds: string[];
  onToggleRow: (record: PolicyManagementRecord) => void;
  onToggleAllRows: () => void;
  columns: StewartTableColumn<PolicyManagementRecord>[];
  hiddenColumns: (keyof PolicyManagementRecord)[];
  onColumnResize: (property: keyof PolicyManagementRecord, newWidth: number) => void;
  onColumnsModified: (next: StewartTableColumn<PolicyManagementRecord>[]) => void;
  summaryComponent?: ReactElement;
  bottomActionsComponent?: ReactElement;
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  onRefresh?: () => void;
  // Controlled expanded-row state, owned by the parent tab.
  expandedRowIds?: string[];
  onExpandedRowIdsChange?: (ids: string[]) => void;
}

const CloseQueueTable = ({
  records,
  selectedIds,
  onToggleRow,
  onToggleAllRows,
  columns,
  hiddenColumns,
  onColumnResize,
  onColumnsModified,
  summaryComponent,
  bottomActionsComponent,
  rowsPerPage: rowsPerPageProp,
  onRowsPerPageChange,
  page: pageProp,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  sortColumn,
  onSortChange,
  onRefresh,
  expandedRowIds,
  onExpandedRowIdsChange,
}: CloseQueueTableProps) => {
  const [internalPage, setInternalPage] = useState<number>(1);
  const loadingPercent = useSteppedProgress(!!isLoading);
  const rowsPerPage = rowsPerPageProp ?? 20;
  const orderBy = useMemo<keyof PolicyManagementRecord | undefined>(() => {
    if (!sortColumn) return undefined;
    const col = sortColumn.startsWith("-") ? sortColumn.slice(1) : sortColumn;
    return col as keyof PolicyManagementRecord;
  }, [sortColumn]);
  const order: Order = sortColumn?.startsWith("-") ? "desc" : "asc";

  const page = pageProp ?? internalPage;
  const useServerPaging = pageProp !== undefined && totalRows !== undefined;

  const handlePageChange = (newPage: number) => {
    setInternalPage(newPage);
    onPageChange?.(newPage);
  };
  const handleRowsPerPageChange = (count: number) => {
    setInternalPage(1);
    onRowsPerPageChange?.(count);
  };
  const handleRequestSort = (property: keyof PolicyManagementRecord) => {
    const propStr = String(property);
    const currentColumn = sortColumn?.startsWith("-")
      ? sortColumn.slice(1)
      : sortColumn;
    const currentDesc = sortColumn?.startsWith("-") ?? false;
    const nextDesc = currentColumn === propStr ? !currentDesc : false;
    const next = nextDesc ? `-${propStr}` : propStr;
    onSortChange?.(next);
  };

  const selectionConfig: SelectionConfig<PolicyManagementRecord> = useMemo(
    () => ({
      enabled: true,
      selectedRows: selectedIds,
      onToggleAllRows,
      onToggleRow,
      isRowSelected: (row: PolicyManagementRecord) =>
        selectedIds.includes(row.uniqueIdentifier),
      isRowDisabled: (row: PolicyManagementRecord) => !!row.isLocked,
    }),
    [selectedIds, onToggleAllRows, onToggleRow]
  );

  const detailsViewConfig: DetailsViewConfig<PolicyManagementRecord> = useMemo(
    () => ({
      enabled: true,
      hideExpandAllButton: true,
      renderRowDetails: (row: PolicyManagementRecord, collapse: () => void) => (
        <PolicyDetailsPanel
          record={row}
          variant="cq"
          onRefresh={onRefresh}
          onCollapse={collapse}
        />
      ),
      expandedRowIds,
      onExpandedRowIdsChange,
    }),
    [onRefresh, expandedRowIds, onExpandedRowIdsChange]
  );

  const emptyStateType =
    records.length === 0 ? EmptyStateType.NO_RESULTS : EmptyStateType.DEFAULT;
  const noRowsMessage = ["No records ready to close."];

  return (
    <Box
      sx={{
        "& .MuiTableCell-root.row-header-cell": {
          width: "100px !important",
          minWidth: "100px",
          maxWidth: "100px",
        },
      }}
    >
      <StewartTable
        cols={columns}
        rows={records}
        page={page}
        rowsPerPage={rowsPerPage}
        showActionRow={true}
        noRowsMessage={noRowsMessage}
        orderBy={orderBy}
        order={order}
        useDbPagingSorting={useServerPaging}
        onRequestSort={handleRequestSort}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        hiddenColumns={hiddenColumns}
        selectionConfig={selectionConfig}
        detailsViewConfig={detailsViewConfig}
        emptyStateType={emptyStateType}
        footerComponent={summaryComponent}
        loadingProgressPercent={loadingPercent}
      />
      <StewartTableFooterPaginator
        totalRows={totalRows ?? records.length}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        disablePagination={(totalRows ?? records.length) === 0}
      />
      {bottomActionsComponent}
    </Box>
  );
};

export default CloseQueueTable;
